import { getAuthToken } from '../utils/cookies.js';
import { API_BASE_URL } from './constants.js';
import { handleUnauthorized, handleRegistrationIncomplete } from './authSession.js';

// An incomplete registration refuses protected routes with 409 + this machine code +
// `nextPath` — never 401 (401 triggers the handler above, which is wrong here: the
// credential is fine, the account just isn't finished yet).
const REGISTRATION_INCOMPLETE_CODE = 'registration_incomplete';

// Get organization unique from browser global data
export const getOrganizationUnique = () => {
  // Try to get from serverData or fall back to parsing URL
  if (window.serverData && window.serverData._org) {
    const org = window.serverData._org;
    const orgMatch = org.match(/\/org\/([^/]+)/);
    return orgMatch ? orgMatch[1] : org;
  }

  // Fallback: parse from current URL
  const urlMatch = window.location.pathname.match(/\/org\/([^/]+)/);
  return urlMatch ? urlMatch[1] : null;
};

export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getAuthToken();

  // Check if body is FormData - don't set Content-Type for multipart uploads
  const isFormData = options.body instanceof FormData;

  const defaultOptions = {
    method: 'GET',
    // holdshort.com and api.holdshort.com are same-site but different origins, so Fetch's
    // 'same-origin' default drops the cross-origin Set-Cookie (the login handoff cookie in
    // particular) and would drop any cookie this client needs to send. Server-side
    // allow_credentials is already true with an explicit origin list.
    credentials: 'include',
    headers: {
      // Don't set Content-Type for FormData - browser will set it with boundary
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      ...(token && { 'token': token }),
    },
    ...options
  };

  // Merge headers if options contain headers
  if (options.headers) {
    defaultOptions.headers = {
      ...defaultOptions.headers,
      ...options.headers,
      ...(token && { 'token': token }), // Ensure token always takes precedence
    };
  }

  try {
    const response = await fetch(url, defaultOptions);

    // 401 = no usable credentials (dead session). 403/400/402 mean the user IS authenticated
    // and must stay logged in, so branch on the status — the 401 and 403 bodies differ only
    // by a capital letter and a period.
    if (response.status === 401) {
      handleUnauthorized();
    }

    if (!response.ok) {
      // An error body is not necessarily JSON (an nginx/proxy 4xx is HTML), and losing the
      // status to a parse error would take the 401/409 handling down with it.
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.status === 409 && data?.data?.code === REGISTRATION_INCOMPLETE_CODE) {
        handleRegistrationIncomplete(data.data.nextPath);
      }

      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.response = { data, status: response.status };
      throw error;
    }

    // 204 No Content (e.g. DELETE) has no body to parse.
    if (response.status === 204) {
      return { data: null };
    }

    const data = await response.json();

    return { data };
  } catch (error) {
    // If it's a fetch error (network issue), rethrow as-is
    if (!error.response) {
      console.error('API request failed:', error);
    }
    throw error;
  }
};